import { PRODUCT_IDENTITY } from "../shared/branding/product-identity.js";
import { getRuntimeBuildInfo } from "../shared/branding/app-version.js";
import "./styles.css";

const buildInfo = getRuntimeBuildInfo();
console.log("[BUILD-RUNTIME]", {
  commit: buildInfo.commit || "(build time)",
  appVersion: buildInfo.appVersion,
  buildTimestamp: buildInfo.buildTimestamp,
  asset: buildInfo.asset,
  href: typeof window !== "undefined" ? window.location.href : ""
});

// Design System
import "./design-system/index.js";

// Init Browser Platform
import "../app/browser-platform.js";

// MOJIBAKE FIXER - Limpeza de dados corrompidos no localStorage (emoji bugs)
import { fixMojibake } from "../app/mojibake-fixer.js";
fixMojibake();

// MIGRATION - Atualização de tubulações antigas
import { migrateOldPipingEquipments } from "../app/equipment-migration.js";
migrateOldPipingEquipments();

// Shell & Page Components
import "./pages/login-page.js";
import "./components/app-shell.js";
import "./components/empty-state.js";
import "./components/error-state.js";
import "./pages/overview-page.js";
// M16A: Home por Perfil substitui Overview como landing page "/".
import "./pages/user-home-page.js";
import "./pages/placeholder-page.js";
import "./pages/studio-page.js";
import "./pages/studio-editor-shell.js";
import "./pages/catalogs-page.js";
import "./pages/administration-hub-page.js";
import "./pages/backup-restore-page.js";
import "./pages/admin-clients-page.js";
import "./pages/admin-areas-page.js";
import "./pages/projects-page.js";
import "./pages/project-detail-page.js";
import "./pages/admin-os-page.js";
import "./pages/admin-fronts-page.js";
import "./pages/management-service-fronts-page.js";
import "./pages/admin-users-page.js";
import "./pages/admin-professionals-page.js";
import "./pages/people-access-page.js";
import "./pages/admin-signatories-page.js";
import "./pages/admin-companies-page.js";
import "./pages/management-hub-page.js";
import "./pages/inspect-ia-review-page.js";
import "./components/ai-review-panel.js";
import "./components/ai-assistant-drawer.js";
import "./pages/engineering-page.js";
import "./pages/engineering-calculations-page.js";
import "./pages/engineering-workspace.js";
import "./pages/equipment-page.js";
import "./pages/equipment-list-page.js";
import "./pages/equipment-detail-page.js";
import "./pages/instruments-page.js";
import "./pages/safety-devices-page.js";
import "./pages/technical-assets-hub-page.js";
import "./pages/ndt-registry-page.js";
import "./pages/lp-report-page.js";
import "./pages/pm-report-page.js";
import "./pages/conventional-us-report-page.js";
import "./pages/bscan-report-page.js";
import "./pages/settings-page.js";
import "./pages/studio-design-page.js";
import { loadAndApplyAppearance, loadPublicAppearance } from "./studio/appearance.js";
import "./pages/ndt-reports-central-page.js";
import "./pages/inspections-page.js";
import "./pages/field-control-page.js";
import "./components/image-annotation-editor.js";
import "./pages/inspection-flow.js";
import "./pages/integrity-reports-page.js";
import "./pages/recommendations-page.js";
import "./pages/recommendations-draft-page.js";
import "./pages/visual-checklist-page.js";
import "./pages/rdo-page.js";
import "./pages/documentation-page.js";
import "./pages/inspection-book-page.js";
import "./pages/equipment-import-page.js";
import "./pages/avr-page.js";
import "./pages/avr-plh-review-page.js";
import "./pages/technical-review-page.js";
import "./pages/me-report-page.js";
import "./pages/report-viewer-page.js";
import "./pages/verify-report-page.js";

// Routing and Auth
import { router } from "./router/router.js";
import { contextStore } from "./auth/context-store.js";
import { ensureActiveClientContext } from "./auth/tenant-context.js";
import * as authGateway from "./auth/auth-gateway.js";
import { PermissionCode } from "../permissions/permissions.js";

// AI Init

document.title = PRODUCT_IDENTITY.name;

// A paleta de execução (PRODUCT_IDENTITY.palette: --color-primary #3C3C3C e a
// escala de grafite) mora no :root do styles.css. Ela era gravada aqui como
// estilo inline no <html> — e o aplicador de aparência do Studio, ao limpar
// as variáveis "iguais ao padrão", apagava essa gravação: --color-primary
// voltava ao ouro #C7A75B e toda superfície que o usa (cabeçalho de tabela,
// painéis) saía dourada. Uma fonte só, no CSS, e o teste confere que ela bate
// com PRODUCT_IDENTITY.

/** Painel compartilhado por link: página pública, fora do login, do shell e do roteador. */
const isSharedDashboardPath = (): boolean => /^\/share\/dashboard\/[^/]+\/?$/.test(window.location.pathname);

async function openSharedDashboard(): Promise<void> {
  // O token está na URL: nenhuma requisição desta página leva o endereço no Referer.
  const referrer = document.createElement('meta');
  referrer.name = 'referrer';
  referrer.content = 'no-referrer';
  document.head.appendChild(referrer);
  const robots = document.createElement('meta');
  robots.name = 'robots';
  robots.content = 'noindex, nofollow';
  document.head.appendChild(robots);
  document.title = `Painel compartilhado — ${PRODUCT_IDENTITY.name}`;
  await import('./pages/shared-dashboard-page.js');
  document.body.replaceChildren(document.createElement('app-shared-dashboard-page'));
}

// Bootstrap Session Validator
async function bootstrap() {
  // STUDIO INSPECT IA: favicon/ícone publicados valem já na tela de login.
  const publicAppearance = loadPublicAppearance();
  // Link compartilhado do Painel: não exige sessão e não passa pelo login.
  if (isSharedDashboardPath()) {
    await openSharedDashboard();
    return;
  }
  const token = localStorage.getItem('inspect_auth_token');
  const isLoginRoute = window.location.pathname === '/login' || window.location.hash.includes('/login');

  if (!authGateway.isBackendMode() && !token) {
    if (!isLoginRoute) {
      window.location.href = '/login';
    } else {
      initializeRouter();
    }
    return;
  }

  // Show a neutral loading screen (avoid flashing dashboard)
  const overlay = document.createElement('div');
  overlay.id = 'boot-loading-overlay';
  overlay.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;width:100vw;background: var(--ia-bg-panel, #111418);color: var(--ia-fg, #F7F8F8);font-family:sans-serif;position:fixed;top:0;left:0;z-index:999999;">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite; margin-bottom: 16px;">
        <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
      </svg>
      <div style="font-size: 14px; letter-spacing: 1px;">VALIDANDO SESSÃO...</div>
      <style>@keyframes spin { 100% { transform: rotate(360deg); } }</style>
    </div>
  `;
  document.body.appendChild(overlay);
  // Logo da aplicação (Studio) na tela de abertura, se houver um publicado.
  void publicAppearance.then(pub => {
    if (!pub?.appLogoUrl || !document.body.contains(overlay)) return;
    const img = document.createElement('img');
    img.src = pub.appLogoUrl;
    img.alt = 'INSPECT IA';
    img.style.cssText = 'position:fixed;top:calc(50% - 110px);left:50%;transform:translateX(-50%);max-height:48px;max-width:240px;z-index:1000000;';
    overlay.appendChild(img);
  });

  try {
    // A revalidação da sessão é feita pela porta de autenticação: no servidor
    // (GET /api/session) em homologação; relendo o usuário no repositório
    // local no teste local — onde não existe /api/session e o Vite devolveria
    // o próprio index.html com 200, fazendo a sessão parecer válida.
    const session = await authGateway.restoreSession();

    if (!session) {
      // Não chamar logout (POST /api/logout em loop). Apenas remover overlay e redirecionar.
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
      if (isLoginRoute) {
        initializeRouter();
      } else {
        window.location.href = '/login';
      }
      return;
    }

    contextStore.login(session.name, session.userId, session.role, {
      professionalId: session.professionalId,
      ...(session.customPermissions ? { customPermissions: session.customPermissions } : {})
    });

    // Validate and rehydrate active client on F5 / session restore
    await ensureActiveClientContext();

    // Identidade publicada no Studio antes da primeira tela — sem "piscar" o tema padrão.
    await loadAndApplyAppearance();

    // Once validated, we can remove the overlay and start the router
    if (document.body.contains(overlay)) {
      document.body.removeChild(overlay);
    }
    initializeRouter();

  } catch (err: any) {
    if (err && err.status === 403) {
      overlay.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;width:100vw;background: var(--ia-bg-panel, #111418);color: var(--ia-fg, #F7F8F8);font-family:sans-serif;position:fixed;top:0;left:0;z-index:999999;">
          <div style="color: var(--ia-danger-fg, #F87171); font-weight: bold; margin-bottom: 16px; font-size: 18px;">ACESSO À HOMOLOGAÇÃO NÃO AUTORIZADO</div>
          <div style="color: var(--ia-danger-fg, #FCA5A5); margin-bottom: 32px; font-size: 14px;">Seu usuário não está autorizado para o ambiente de homologação.</div>
          <button onclick="localStorage.removeItem('inspect_auth_token'); window.location.href='/login';" style="padding: 10px 20px; background: transparent; border: 1px solid #fff; color: var(--ia-fg, #fff); cursor: pointer; border-radius: 4px;">VOLTAR AO LOGIN</button>
        </div>
      `;
      return; // Halt here, do not redirect to login automatically to avoid loop
    }
    // Erro na restauração da sessão: redirecionar para login sem chamar logout
    if (document.body.contains(overlay)) {
      document.body.removeChild(overlay);
    }
    localStorage.removeItem('inspect_auth_token');
    if (isLoginRoute) {
      initializeRouter();
    } else {
      window.location.href = '/login';
    }
  }
}

// Global BFCache / Tab syncing
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // If the browser restores the page from BFCache after logout
    if (!localStorage.getItem('inspect_auth_token') && window.location.pathname !== '/login' && !isSharedDashboardPath()) {
      window.location.reload();
    }
  }
});
window.addEventListener('storage', (event) => {
  if (event.key === 'inspect_auth_token' && !event.newValue) {
    // Another tab logged out (o painel compartilhado não depende de sessão)
    if (window.location.pathname !== '/login' && !isSharedDashboardPath()) {
      window.location.reload();
    }
  }
});

function initializeRouter() {
// Initialize routing
router.init([
  { path: "/login", component: "app-login-page" },
  // M16A: "/" é agora a Home por Perfil; Painel Geral movido para /dashboard/indicators com gate RBAC.
  { path: "/", component: "app-user-home-page" },
  { path: "/dashboard/indicators", component: "app-overview-page", requiredPermission: PermissionCode.DashboardIndicatorsView },
  { path: "/technical-assets", component: "app-technical-assets-hub-page", requiredPermission: PermissionCode.EquipmentRead },
  { path: "/equipment", component: "app-equipment-list-page", requiredPermission: PermissionCode.EquipmentRead },
  { path: "/equipment/new", component: "app-equipment-list-page", requiredPermission: PermissionCode.EquipmentCreate },
  // Antes de /equipment/:id — caso contrário o parâmetro capturaria "import".
  { path: "/equipment/import", component: "app-equipment-import-page", requiredPermission: PermissionCode.EquipmentImport },
  { path: "/equipment/:id", component: "app-equipment-detail-page", requiredPermission: PermissionCode.EquipmentRead },
  { path: "/instruments", component: "app-instruments-page", requiredPermission: PermissionCode.InstrumentRead },
  { path: "/safety-devices", component: "app-safety-devices-page", requiredPermission: PermissionCode.SafetyDeviceRead },
  { path: "/inspections", component: "app-inspections-page", requiredPermission: PermissionCode.InspectionRead },
  // Antes de /inspections/:id — senão o parâmetro capturaria "field-control".
  { path: "/inspections/field-control", component: "app-field-control-page", requiredPermission: PermissionCode.FieldControlViewOwn },
  { path: "/inspections/new", component: "app-visual-checklist-page", requiredPermission: PermissionCode.InspectionCreate },

  { path: "/reports", component: "app-inspections-page", requiredPermission: PermissionCode.ReportRead },
  { path: "/reports/integrity", component: "app-inspections-page", requiredPermission: PermissionCode.ReportRead },
  { path: "/reports/integrity/view/:id", component: "app-report-viewer-page", requiredPermission: PermissionCode.ReportRead },
  // "/reports/view/:id" e "/integrity/view/:id" removidas (Bloco 05): rotas
  // órfãs, sem nenhuma navegação real apontando para elas (só existiam para
  // o item de menu "Visualizador de Laudo", removido por usar um id fixo
  // fictício). "/reports/integrity/view/:id" continua sendo a visualização
  // real, usada a partir de Relatórios de Integridade/Recomendações/Checklist.
  { path: "/reports/preview/:id", component: "app-report-viewer-page", requiredPermission: PermissionCode.ReportRead },
  { path: "/reports/ndt", component: "app-ndt-reports-central-page", requiredPermission: PermissionCode.NdtRead },
  { path: "/verify-report/:token", component: "app-verify-report-page" },

  { path: "/reports/integrity/checklist/:id", component: "app-visual-checklist-page", requiredPermission: PermissionCode.ChecklistRead },
  { path: "/inspections/checklist/:id", component: "app-visual-checklist-page", requiredPermission: PermissionCode.ChecklistRead },
  { path: "/checklist", component: "app-visual-checklist-page", requiredPermission: PermissionCode.ChecklistRead },
  // "Prontuário Técnico de Inspeção" foi DESCONTINUADO (2026-09-26): /integrity/prontuario e
  // /prontuario não existem mais (404 controlado do roteador). A raiz da Integridade, que abria
  // aquela tela, leva à primeira área válida do grupo.
  { path: "/integrity", redirect: "/integrity/recommendations" },
  { path: "/integrity/reports", component: "app-inspections-page", requiredPermission: PermissionCode.ReportRead },

  { path: "/integrity/recommendations", component: "app-recommendations-page", requiredPermission: PermissionCode.RecommendationRead },
  { path: "/integrity/recommendations-draft", component: "app-recommendations-draft-page", requiredPermission: PermissionCode.RecommendationRead },
  { path: "/integrity/avr", component: "app-avr-page", requiredPermission: PermissionCode.AvrRead },
  { path: "/integrity/avr/:id", component: "app-avr-page", requiredPermission: PermissionCode.AvrRead },
  { path: "/engineering", component: "app-engineering-page", requiredPermission: PermissionCode.EngineeringAccess },
  { path: "/engineering/workspace/:id", component: "app-engineering-workspace", requiredPermission: PermissionCode.EngineeringAccess },
  { path: "/engineering/avr/:id", component: "app-avr-plh-review-page", requiredPermission: PermissionCode.AvrRead },
  { path: "/engineering/review/:id", component: "app-technical-review-page", requiredPermission: PermissionCode.TechnicalReviewViewQueue },
  { path: "/engineering/calculations", component: "app-engineering-calculations-page", requiredPermission: PermissionCode.CalculationAccess },
  { path: "/management", component: "app-management-hub-page", requiredPermission: PermissionCode.ManagementAccess },
  { path: "/management/clients", component: "app-admin-clients-page", requiredPermission: PermissionCode.ClientManage },
  { path: "/management/projects", component: "app-projects-page", requiredPermission: PermissionCode.ProjectManage },
  { path: "/management/projects/:id", component: "app-project-detail-page", requiredPermission: PermissionCode.ProjectManage },
  { path: "/management/areas", redirect: "/management/projects" },
  { path: "/management/os", component: "app-admin-os-page", requiredPermission: PermissionCode.ManagementAccess },
  { path: "/management/fronts", component: "app-management-service-fronts-page", requiredPermission: PermissionCode.ManagementAccess },
  { path: "/management/service-fronts", redirect: "/management/fronts" },
  { path: "/management/rdo", component: "app-rdo-page", requiredPermission: PermissionCode.RdoRead },
  { path: "/documentation", component: "app-documentation-page", requiredPermission: PermissionCode.DocumentRead },
  { path: "/documentation/library", component: "app-documentation-page", requiredPermission: PermissionCode.DocumentRead },
  { path: "/documentation/attach", component: "app-documentation-page", requiredPermission: PermissionCode.DocumentWrite },
  { path: "/documentation/book", component: "app-inspection-book-page", requiredPermission: PermissionCode.EquipmentRead },

  { path: "/administration", component: "app-administration-hub-page", requiredPermission: PermissionCode.AdministrationRead },
  // CONFIGURAÇÕES — cada aba e cada gravação conferem a própria permissão no serviço.
  { path: "/settings", component: "app-settings-page", requiredPermission: PermissionCode.SettingsRead },
  // STUDIO INSPECT IA — cada aba e cada gravação conferem a própria permissão no serviço.
  { path: "/studio", component: "app-studio-design-page", requiredPermission: PermissionCode.StudioRead },
  { path: "/ndt/equipment", component: "app-ndt-registry-page", requiredPermission: PermissionCode.NdtRegistryRead },
  { path: "/ndt/thickness", component: "app-me-report-page", requiredPermission: PermissionCode.MeReportView },
  { path: "/ndt/thickness/:id", component: "app-me-report-page", requiredPermission: PermissionCode.MeReportView },
  { path: "/ndt/lp", component: "app-lp-report-page", requiredPermission: PermissionCode.LpReportView },
  { path: "/ndt/lp/:id", component: "app-lp-report-page", requiredPermission: PermissionCode.LpReportView },
  { path: "/ndt/pm", component: "app-pm-report-page", requiredPermission: PermissionCode.PmReportView },
  { path: "/ndt/pm/:id", component: "app-pm-report-page", requiredPermission: PermissionCode.PmReportView },
  { path: "/ndt/us-conventional", component: "app-conventional-us-report-page", requiredPermission: PermissionCode.UscReportView },
  { path: "/ndt/us-conventional/:id", component: "app-conventional-us-report-page", requiredPermission: PermissionCode.UscReportView },
  { path: "/ndt/bscan", component: "app-bscan-report-page", requiredPermission: PermissionCode.BscanReportView },
  { path: "/ndt/bscan/:id", component: "app-bscan-report-page", requiredPermission: PermissionCode.BscanReportView },

  // Redirects from old Administration/Areas routes to Management/Projects
  { path: "/administration/clients", redirect: "/management/clients" },
  { path: "/administration/areas", redirect: "/management/projects" },
  { path: "/areas", redirect: "/management/projects" },
  { path: "/projects", redirect: "/management/projects" },
  { path: "/administration/os", redirect: "/management/os" },
  { path: "/administration/fronts", redirect: "/management/fronts" },
  
  // Remaining Administration routes
  // PESSOAS E ACESSOS — cadastro único (Professional como fonte mestre). As
  // três telas antigas continuam roteadas enquanto esta é validada (item 38).
  { path: "/administration/people", component: "app-people-access-page", requiredPermission: PermissionCode.IdentityRead },
  { path: "/administration/users", component: "app-admin-users-page", requiredPermission: PermissionCode.UserRead },
  { path: "/administration/companies", component: "app-admin-companies-page", requiredPermission: PermissionCode.AdministrationRead },
  { path: "/administration/professionals", component: "app-admin-professionals-page", requiredPermission: PermissionCode.IdentityManage },
  { path: "/administration/signatories", component: "app-admin-signatories-page", requiredPermission: PermissionCode.IdentityManage },
  { path: "/administration/backup", component: "app-backup-restore-page", requiredPermission: PermissionCode.BackupRead },

  { path: "/studio-editor/:id/:versionId", component: "app-studio-editor-shell", requiredPermission: PermissionCode.StudioUpdate },
  // INSPECT IA REVIEW — IA oficial. O Córtex foi descontinuado: /cortex redireciona.
  { path: "/ai-review", component: "app-inspect-ia-review-page", requiredPermission: PermissionCode.AiReviewRead },
  { path: "/cortex", redirect: "/ai-review" },
], "#page-root", (perm) => contextStore.hasPermission(perm));
}

bootstrap();
